import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { setAdvancedFilters, clearAdvancedFilters, executeSearch } from '@/features/search/searchSlice';
import { trackEvent } from '@/services/analyticsService';
import { ANALYTICS_EVENTS } from '@/config/analyticsEvents';
import type { SourceSystem } from '@/types';
import styles from './SearchFilterPanel.module.css';

const SOURCE_OPTIONS: { value: SourceSystem; label: string }[] = [
  { value: 'beehive_upload', label: 'Uploads' },
  { value: 'queenbee_media', label: 'QueenBee Media' },
  { value: 'queenbee_bandbox', label: 'BandBox' },
  { value: 'mayfly', label: 'Mayfly' },
];

// ─── unit helpers ─── panel shows friendly units; wire shape is ms / bytes ────
const msToSec = (ms: number | null): string => (ms != null ? String(Math.round(ms / 1000)) : '');
const secToMs = (s: string): number | null => (s.trim() === '' ? null : Math.max(0, Math.round(Number(s) * 1000)));
const bytesToMb = (b: number | null): string => (b != null ? String(Math.round(b / 1_000_000)) : '');
const mbToBytes = (s: string): number | null => (s.trim() === '' ? null : Math.max(0, Math.round(Number(s) * 1_000_000)));

/** Count of constrained facets — drives the toggle badge. */
function countActive(f: {
  source_system: SourceSystem[];
  date_from: string | null;
  date_to: string | null;
  duration_min_ms: number | null;
  duration_max_ms: number | null;
  size_min_bytes: number | null;
  size_max_bytes: number | null;
  speaker: string | null;
}): number {
  let n = 0;
  if (f.source_system.length) n += 1;
  if (f.date_from || f.date_to) n += 1;
  if (f.duration_min_ms != null || f.duration_max_ms != null) n += 1;
  if (f.size_min_bytes != null || f.size_max_bytes != null) n += 1;
  if (f.speaker && f.speaker.trim()) n += 1;
  return n;
}

export default function SearchFilterPanel() {
  const dispatch = useAppDispatch();
  const query = useAppSelector((s) => s.search.query);
  const advancedFilters = useAppSelector((s) => s.search.advancedFilters);

  const [open, setOpen] = useState(false);

  // Local draft for the text/number/date inputs — applied on demand so we don't
  // fire a search on every keystroke (per the analytics rule on typed inputs).
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [durMin, setDurMin] = useState('');
  const [durMax, setDurMax] = useState('');
  const [sizeMin, setSizeMin] = useState('');
  const [sizeMax, setSizeMax] = useState('');
  const [speaker, setSpeaker] = useState('');

  // Keep the draft in sync when the store changes (e.g. Clear all / new search).
  useEffect(() => {
    setDateFrom(advancedFilters.date_from ?? '');
    setDateTo(advancedFilters.date_to ?? '');
    setDurMin(msToSec(advancedFilters.duration_min_ms));
    setDurMax(msToSec(advancedFilters.duration_max_ms));
    setSizeMin(bytesToMb(advancedFilters.size_min_bytes));
    setSizeMax(bytesToMb(advancedFilters.size_max_bytes));
    setSpeaker(advancedFilters.speaker ?? '');
  }, [advancedFilters]);

  const activeCount = countActive(advancedFilters);

  const rerun = () => {
    if (query.length >= 2) dispatch(executeSearch({ query, limit: 15 }));
  };

  const handleToggleOpen = () => {
    const next = !open;
    setOpen(next);
    trackEvent(ANALYTICS_EVENTS.SEARCH_FILTER_PANEL_TOGGLE, { open: next });
  };

  const toggleSource = (value: SourceSystem) => {
    const set = advancedFilters.source_system;
    const next = set.includes(value) ? set.filter((v) => v !== value) : [...set, value];
    trackEvent(ANALYTICS_EVENTS.SEARCH_SOURCE_FILTER, { source: value, active: !set.includes(value), count: next.length });
    dispatch(setAdvancedFilters({ source_system: next }));
    rerun();
  };

  const handleApply = () => {
    const patch = {
      date_from: dateFrom || null,
      date_to: dateTo || null,
      duration_min_ms: secToMs(durMin),
      duration_max_ms: secToMs(durMax),
      size_min_bytes: mbToBytes(sizeMin),
      size_max_bytes: mbToBytes(sizeMax),
      speaker: speaker.trim() || null,
    };
    trackEvent(ANALYTICS_EVENTS.SEARCH_ADVANCED_FILTER_APPLY, {
      hasDate: !!(patch.date_from || patch.date_to),
      hasDuration: patch.duration_min_ms != null || patch.duration_max_ms != null,
      hasSize: patch.size_min_bytes != null || patch.size_max_bytes != null,
      hasSpeaker: !!patch.speaker,
    });
    dispatch(setAdvancedFilters(patch));
    rerun();
  };

  const handleClearAll = () => {
    trackEvent(ANALYTICS_EVENTS.SEARCH_ADVANCED_FILTER_CLEAR);
    dispatch(clearAdvancedFilters());
    rerun();
  };

  return (
    <div className={styles.wrap}>
      <button
        className={`${styles.toggle} ${open || activeCount > 0 ? styles.toggleActive : ''}`}
        onClick={handleToggleOpen}
        aria-expanded={open}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
        Filters
        {activeCount > 0 && <span className={styles.badge}>{activeCount}</span>}
      </button>

      {open && (
        <div className={styles.panel} role="group" aria-label="Advanced search filters">
          {/* Source system — multi-select, applies immediately */}
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Source</span>
            <div className={styles.chipRow}>
              {SOURCE_OPTIONS.map((opt) => {
                const active = advancedFilters.source_system.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    className={`${styles.chip} ${active ? styles.chipActive : ''}`}
                    onClick={() => toggleSource(opt.value)}
                    aria-pressed={active}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date range */}
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Date</span>
            <div className={styles.inputRow}>
              <input type="date" className={styles.input} value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} aria-label="From date" />
              <span className={styles.sep}>–</span>
              <input type="date" className={styles.input} value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} aria-label="To date" />
            </div>
          </div>

          {/* Duration (seconds) — videos only */}
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Duration (s)</span>
            <div className={styles.inputRow}>
              <input type="number" min="0" className={styles.inputNum} placeholder="Min" value={durMin} onChange={(e) => setDurMin(e.target.value)} aria-label="Minimum duration in seconds" />
              <span className={styles.sep}>–</span>
              <input type="number" min="0" className={styles.inputNum} placeholder="Max" value={durMax} onChange={(e) => setDurMax(e.target.value)} aria-label="Maximum duration in seconds" />
            </div>
          </div>

          {/* File size (MB) */}
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Size (MB)</span>
            <div className={styles.inputRow}>
              <input type="number" min="0" className={styles.inputNum} placeholder="Min" value={sizeMin} onChange={(e) => setSizeMin(e.target.value)} aria-label="Minimum size in MB" />
              <span className={styles.sep}>–</span>
              <input type="number" min="0" className={styles.inputNum} placeholder="Max" value={sizeMax} onChange={(e) => setSizeMax(e.target.value)} aria-label="Maximum size in MB" />
            </div>
          </div>

          {/* Speaker (substring) */}
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Speaker</span>
            <input
              type="text"
              className={styles.input}
              placeholder="Name contains…"
              value={speaker}
              onChange={(e) => setSpeaker(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleApply(); }}
              aria-label="Speaker name contains"
            />
          </div>

          <div className={styles.actions}>
            <button className={styles.applyBtn} onClick={handleApply}>Apply</button>
            {activeCount > 0 && (
              <button className={styles.clearBtn} onClick={handleClearAll}>Clear all</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
